import React, {Fragment, useState, useEffect} from "react";
import Card from "components/Cards/Card";
import trendScoresIcon from "assets/Icon_Tab_09.png";
import {Dropdown, SelectOption} from "components/ui/Dropdown";
import {setChartSelection, getChartSelection} from "helpers/chartSelections";
import {v4 as uuidv4} from "uuid";
import {
  Bar,
  CartesianGrid,
  ComposedChart,
  LabelList,
  Line,
  ResponsiveContainer,
  Text,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import {useCurrentPng} from "recharts-to-png";
import {ImageMap} from "../../Dashboard/Dashboard";
import LoadingIndicator from "../../ui/LoadingIndicator";
import PortalTooltip from "../PortalTooltip";

type ComboChartProps = {
  href: string | null;
  data: ChartData;
  defaultChartType: string;
  questions: Questions[];
  customName?: string;
  setImages?: React.Dispatch<React.SetStateAction<ImageMap>>;
  idx?: number;
  isLoading?: boolean;
};

type Questions = {
  value: string;
  label: string;
};

type ChartData = {
  pieChartData: any;
  otherChartData: any[];
  options: {
    yAxisType: "Count" | "Percentage" | "Score";
  };
  questions?: string[];
  periods?: string[];
};

// Score line uses the primary brand colour; the response-count bars sit
// behind it in a muted/neutral grey so the trend line reads clearly.
const lineColour = "#307bb9";
const barColour = "#cbd5dd";

const checkNullChartData = (c: any[]) => {
  const data = c.find((v) => v);
  return !!data;
};

function percentageFormatter(item: any) {
  return parseFloat(item).toFixed(1) + "%";
}

function avgFormatter(item: any) {
  return item.toFixed(1);
}

function scoreFormatter(item: any) {
  return parseFloat(item).toFixed(2);
}

function countFormatter(item: any) {
  return Math.round(item).toString();
}

const scoreTicks = [0, 1, 2, 3, 4, 5];
const scoreDomain: [number, number] = [0, 5];
const ticks20 = [0, 20, 40, 60, 80, 100];

function getFormatter(yAxisType: string | undefined) {
  if (yAxisType === "Percentage") return percentageFormatter;
  if (yAxisType === "Score") return scoreFormatter;
  return avgFormatter;
}

function getMinValue(data: any[], keys?: string[]): number {
  let min = 0;
  if (!data || !data.length) return 0;
  data.forEach((item: any) => {
    if (keys && keys.length) {
      keys.forEach((k) => {
        const v = item[k];
        if (typeof v === "number" && v < min) min = v;
      });
    }
  });
  return min;
}

function getPercentageDomain(data: any[], keys?: string[]): [number, number] {
  const min = getMinValue(data, keys);
  if (min >= 0) return [0, 100];
  const roundedMin = Math.floor(min / 20) * 20;
  return [Math.max(roundedMin, -100), 100];
}

function getPercentageTicks(data: any[], keys?: string[]): number[] {
  const min = getMinValue(data, keys);
  if (min >= 0) return ticks20;
  const roundedMin = Math.floor(min / 20) * 20;
  const start = Math.max(roundedMin, -100);
  const ticks: number[] = [];
  for (let i = start; i <= 100; i += 20) {
    ticks.push(i);
  }
  return ticks;
}

function getScoreYAxisDomain(yAxisType: string | undefined, data: any[], keys?: string[]) {
  if (yAxisType === "Percentage") return getPercentageDomain(data, keys);
  if (yAxisType === "Score") return scoreDomain;
  return undefined;
}

function getScoreYAxisTicks(yAxisType: string | undefined, data: any[], keys?: string[]) {
  if (yAxisType === "Percentage") return getPercentageTicks(data, keys);
  if (yAxisType === "Score") return scoreTicks;
  return undefined;
}

const TrendTick: React.FC<any> = ({
                                    payload: {value},
                                    verticalAnchor,
                                    visibleTicksCount,
                                    ...rest
                                  }) => (
    <Text {...rest} verticalAnchor={verticalAnchor} visibleTicksCount={visibleTicksCount} width={50}>
      {value}
    </Text>
);

const ComboChart: React.FC<ComboChartProps> = ({
                                                 href,
                                                 data,
                                                 questions,
                                                 customName,
                                                 setImages,
                                                 idx,
                                                 isLoading = true,
                                               }) => {
  // Track internal loading state
  const [internalLoading, setInternalLoading] = useState(true);

  useEffect(() => {
    // Resolve loading as soon as ANY data shape arrives - including the
    // empty/fallback shape that the dashboard sends before a question is
    // configured. Otherwise the spinner runs forever.
    if (data) {
      const timer = setTimeout(() => {
        setInternalLoading(false);
      }, 300);
      return () => clearTimeout(timer);
    }
  }, [data]);

  const showLoading = isLoading || internalLoading;

  let comboCategories = [
    {
      label: "",
      value: "",
    },
  ];

  const chartId = `comboChart_${customName || "default"}`;

  const [comboCategory, setComboCategory] = useState<SelectOption>(() => {
    const stored = getChartSelection(chartId);
    return stored || comboCategories[0];
  });

  const handleComboCategorySelect = (selectedOption: SelectOption) => {
    setComboCategory(selectedOption);
    setChartSelection(chartId, selectedOption);
  };

  // Listen for sync event from PDF generator
  useEffect(() => {
    const handleSync = () => {
      const stored = getChartSelection(chartId);
      if (stored) setComboCategory(stored);
    };
    window.addEventListener("pdf-sync-selections", handleSync);
    return () => window.removeEventListener("pdf-sync-selections", handleSync);
  }, [chartId]);

  if (questions) {
    comboCategories = [];
    questions.forEach((q: any) => {
      comboCategories.push(q);
    });
    if (comboCategory.label === "" && comboCategories.length)
      setComboCategory(comboCategories[0]);
  }

  const uuid = uuidv4();

  const [getPng, {ref}] = useCurrentPng();
  const [savedPng, setSavedPng] = useState<boolean>(false);

  async function saveChart() {
    let runningInterval = setInterval(async () => {
      if (savedPng) {
        clearInterval(runningInterval);
      }
      try {
        const png = await getPng();

        if (png && setImages && idx) {
          setImages((oldState: ImageMap) => {
            return {...oldState, [idx]: png};
          });
          setSavedPng(true);
          clearInterval(runningInterval);
        }
      } catch (error) {
        // console.log(error)
      }
    }, 1000);
  }

  // If still loading, show the loading indicator regardless of data state
  if (showLoading) {
    return (
        <Card
            href={href}
            icon={trendScoresIcon}
            title={customName ? customName : "COMBO CHART"}
        >
          <div className="CardBody">
            <div style={{display: "flex", justifyContent: "center", padding: "10px 15px 0", overflow: "hidden"}}>
              <div style={{maxWidth: "100%", width: "250px"}}>
                <Dropdown
                    name="ComboCategory"
                    value={comboCategory}
                    options={comboCategories}
                    type="FullWidth"
                    onChange={handleComboCategorySelect}
                />
              </div>
            </div>
            <div className="Chart" style={{height: "300px"}}>
              <LoadingIndicator />
            </div>
          </div>
        </Card>
    );
  }

  // Render the card with a "No results found" message instead of disappearing
  // entirely when the dataset is missing or too sparse - otherwise the user
  // can't tell whether the panel is broken or just empty.
  if (!data?.otherChartData || data?.otherChartData.length < 2) {
    return (
        <Card
            href={href}
            icon={trendScoresIcon}
            title={customName ? customName : "COMBO CHART"}
        >
          <div className="CardBody">
            <div style={{display: "flex", justifyContent: "center", padding: "10px 15px 0", overflow: "hidden"}}>
              <div style={{maxWidth: "100%", width: "250px"}}>
                <Dropdown
                    name="ComboCategory"
                    value={comboCategory}
                    options={comboCategories}
                    type="FullWidth"
                    onChange={handleComboCategorySelect}
                />
              </div>
            </div>
            <div className="Chart" style={{height: "300px"}}>
              <div className="NoChartData">
                <p>No results found</p>
              </div>
            </div>
          </div>
        </Card>
    );
  }

  const scoreKey = comboCategory.label;
  const responsesKey = comboCategory.label + "__responses";
  const scoreFormatterFn = getFormatter(data.options.yAxisType);

  // The Bar series is named "Responses"; format its tooltip value as a raw
  // integer count. The Line series (the question label) gets the configured
  // score/percentage formatter.
  const tooltipFormatter = (value: any, name: string) => {
    if (name === "Responses") return countFormatter(value);
    return scoreFormatterFn(value);
  };

  return (
      <Card
          href={href}
          icon={trendScoresIcon}
          title={customName ? customName : "COMBO CHART"}
      >
        <div className="CardBody">
          <div style={{display: "flex", justifyContent: "center", padding: "10px 15px 0", overflow: "hidden"}}>
            <div style={{maxWidth: "100%", width: "250px"}}>
              <Dropdown
                  name="ComboCategory"
                  value={comboCategory}
                  options={comboCategories}
                  type="FullWidth"
                  onChange={handleComboCategorySelect}
              />
            </div>
          </div>
          <div className="Chart" style={{height: "300px"}}>
            <div className="ChartWrapper">
              {data && checkNullChartData(data.pieChartData.data) ? (() => {
                const calcMw = 100 * data.pieChartData.periods.length - 2;
                return <div
                    className={calcMw > 400 ? "scrollable-chart" : undefined}
                    style={{ minWidth: `${calcMw}px` }}
                >
                  <ResponsiveContainer width="99%" height={270} key={uuid}>
                    <ComposedChart
                        data={data.otherChartData}
                        margin={{top: 20, bottom: 20, left: 20, right: 20}}
                        maxBarSize={65}
                        barGap={15}
                        ref={ref}
                    >
                      <CartesianGrid vertical={false} strokeDasharray="3"/>
                      <Tooltip
                          content={<PortalTooltip formatter={tooltipFormatter} />}
                      />
                      <XAxis
                          dataKey="name"
                          tick={<TrendTick/>}
                          axisLine={false}
                          tickLine={false}
                          padding={{left: 50, right: 50}}
                      />
                      {/* Left axis: hidden but still drives line scaling and
                          the horizontal grid spacing. Each line point shows
                          its score via the LabelList below, so the axis tick
                          labels would be redundant. */}
                      <YAxis
                          yAxisId="score"
                          hide={true}
                          domain={getScoreYAxisDomain(data.options.yAxisType, data.otherChartData, [scoreKey])}
                          ticks={getScoreYAxisTicks(data.options.yAxisType, data.otherChartData, [scoreKey])}
                      />
                      {/* Right axis: hidden but still drives bar scaling.
                          The response-count value itself is shown via a
                          LabelList inside each bar, so the axis labels would
                          be redundant clutter. */}
                      <YAxis
                          yAxisId="responses"
                          orientation="right"
                          hide={true}
                          allowDecimals={false}
                      />
                      {/* Bar declared first so the Line paints on top of it */}
                      <Bar
                          yAxisId="responses"
                          dataKey={responsesKey}
                          name="Responses"
                          fill={barColour}
                          radius={[3, 3, 0, 0]}
                          isAnimationActive={false}
                      >
                        <LabelList
                            dataKey={responsesKey}
                            position="insideBottom"
                            offset={6}
                            formatter={countFormatter}
                            fill="#3a4550"
                            fontSize={11}
                            fontWeight={600}
                        />
                      </Bar>
                      <Line
                          yAxisId="score"
                          dataKey={scoreKey}
                          fill={lineColour}
                          isAnimationActive={false}
                          type="monotone"
                          stroke={lineColour}
                          dot={{
                            stroke: lineColour,
                            strokeWidth: 2,
                          }}
                      >
                        <LabelList
                            dataKey={scoreKey}
                            position="top"
                            formatter={data ? scoreFormatterFn : avgFormatter}
                        />
                      </Line>
                    </ComposedChart>
                  </ResponsiveContainer>
                </div>;
              })() : (
                  <div className="NoChartData">
                    <p>No results found</p>
                  </div>
              )}
            </div>
          </div>
        </div>
      </Card>
  );
};

export default ComboChart;
